import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Plane, 
  Map, 
  FileText as PassportIcon, 
  Ticket, 
  Calendar, 
  Hotel, 
  Car, 
  Plus,
  Search,
  Filter,
  Globe,
  AlertCircle,
  FileText,
  MapPin,
  Clock,
  DollarSign,
  ExternalLink,
  Compass
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { TravelDashboard } from './TravelDashboard';
import { Trips } from './Trips';
import { Passports } from './Passports';
import { Tickets } from './Tickets';
import { Itineraries } from './Itineraries';
import { Accommodations } from './Accommodations';
import { Transportation } from './Transportation';
import { TravelDocuments } from './TravelDocuments';
import { TravelSettings } from './TravelSettings';

interface Trip {
  id: string;
  title: string;
  destination?: string;
  start_date: string;
  end_date: string;
  status: 'planning' | 'booked' | 'in_progress' | 'completed' | 'cancelled';
  budget?: number;
  currency?: string;
  description?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

interface TravelCounts {
  trips: number;
  accommodations: number;
  transportation: number;
  documents: number;
  bookings: number;
  passports: number;
  itineraries: number;
}

export function TravelManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [travelCounts, setTravelCounts] = useState<TravelCounts>({
    trips: 0,
    accommodations: 0,
    transportation: 0,
    documents: 0,
    bookings: 0,
    passports: 0,
    itineraries: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const loadTravelCounts = async () => {
    if (!user) {
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Try to load counts, but don't fail if tables don't exist
      try {
        const [
          tripsResponse,
          accommodationsResponse,
          transportationResponse,
          documentsResponse,
          bookingsResponse,
          passportsResponse,
          itinerariesResponse
        ] = await Promise.allSettled([
          supabase
            .from('trips')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id),
          supabase
            .from('accommodations')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id),
          supabase
            .from('transportation')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id),
          supabase
            .from('travel_documents')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id),
          supabase
            .from('travel_bookings')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id),
          supabase
            .from('passports')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id),
          supabase
            .from('itineraries')
            .select('id', { count: 'exact' })
            .eq('user_id', user.id)
        ]);

        // Handle each response individually
        const trips = tripsResponse.status === 'fulfilled' && !tripsResponse.value.error 
          ? tripsResponse.value.count || 0 
          : 0;
        
        const accommodations = accommodationsResponse.status === 'fulfilled' && !accommodationsResponse.value.error 
          ? accommodationsResponse.value.count || 0 
          : 0;
        
        const transportation = transportationResponse.status === 'fulfilled' && !transportationResponse.value.error 
          ? transportationResponse.value.count || 0 
          : 0;
        
        const documents = documentsResponse.status === 'fulfilled' && !documentsResponse.value.error 
          ? documentsResponse.value.count || 0 
          : 0;
        
        const bookings = bookingsResponse.status === 'fulfilled' && !bookingsResponse.value.error 
          ? bookingsResponse.value.count || 0 
          : 0;

        const passports = passportsResponse.status === 'fulfilled' && !passportsResponse.value.error 
          ? passportsResponse.value.count || 0 
          : 0;

        const itineraries = itinerariesResponse.status === 'fulfilled' && !itinerariesResponse.value.error 
          ? itinerariesResponse.value.count || 0 
          : 0;

        setTravelCounts({
          trips,
          accommodations,
          transportation,
          documents,
          bookings,
          passports,
          itineraries
        });

      } catch (dbError) {
        console.warn('Database queries failed, showing empty counts:', dbError);
        // Set empty counts and continue
        setTravelCounts({
          trips: 0,
          accommodations: 0,
          transportation: 0,
          documents: 0,
          bookings: 0,
          passports: 0,
          itineraries: 0
        });
      }

    } catch (err) {
      console.error('Error in loadTravelCounts:', err);
      // Set empty counts and continue
      setTravelCounts({
        trips: 0,
        accommodations: 0,
        transportation: 0,
        documents: 0,
        bookings: 0,
        passports: 0,
        itineraries: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const navItems: NavItem[] = [
    { 
      id: 'trips', 
      label: 'Trips', 
      icon: <Plane size={24} />,
      description: 'Manage your travel trips and itineraries',
      color: 'bg-blue-50 text-blue-600'
    },
    { 
      id: 'accommodations', 
      label: 'Accommodations', 
      icon: <Hotel size={24} />,
      description: 'Hotels, stays, and lodging',
      color: 'bg-amber-50 text-amber-600'
    },
    { 
      id: 'transportation', 
      label: 'Transportation', 
      icon: <Car size={24} />,
      description: 'Flights, trains, and rental cars',
      color: 'bg-green-50 text-green-600'
    },
    { 
      id: 'documents', 
      label: 'Travel Documents', 
      icon: <FileText size={24} />,
      description: 'Passports, visas, and important docs',
      color: 'bg-purple-50 text-purple-600'
    },
    { 
      id: 'passports', 
      label: 'Passports', 
      icon: <PassportIcon size={24} />,
      description: 'Manage passport information',
      color: 'bg-indigo-50 text-indigo-600'
    },
    { 
      id: 'tickets', 
      label: 'Tickets & Bookings', 
      icon: <Ticket size={24} />,
      description: 'Activities, events, and reservations',
      color: 'bg-rose-50 text-rose-600'
    },
    { 
      id: 'itineraries', 
      label: 'Itineraries', 
      icon: <Calendar size={24} />,
      description: 'Daily plans and activities',
      color: 'bg-teal-50 text-teal-600'
    }
  ];

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setError('User not authenticated. Please log in again.');
      return;
    }
    
    if (user) {
      loadTravelCounts();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => {
      if (user) {
        loadTravelCounts();
      }
    };
    window.addEventListener('refresh-travel', handler);
    return () => window.removeEventListener('refresh-travel', handler);
  }, [user]);

  const handleNavigation = (id: string) => {
    navigate(`/dashboard/travel/${id}`);
  };

  const handleAddTrip = () => {
    navigate('/dashboard/travel/trips');
  };

  return (
    <Routes>
      <Route path="/" element={
        <div className="p-6 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Travel Management</h1>
              <p className="text-gray-600">Plan and manage your travel trips and documents</p>
            </div>
            <div className="flex gap-4">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search travel items..."
              />
              <Button
                variant="outline"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-idecide-primary text-idecide-dark' : ''}
              >
                All
              </Button>
              <Button
                variant="primary"
                onClick={handleAddTrip}
                icon={<Plus size={20} />}
              >
                New Trip
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Plane className="text-blue-600" size={20} />
                <span className="font-medium text-gray-900">Total Trips</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{travelCounts.trips}</p>
              <p className="text-sm text-gray-600 mt-1">Active trips</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Hotel className="text-amber-600" size={20} />
                <span className="font-medium text-gray-900">Accommodations</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{travelCounts.accommodations}</p>
              <p className="text-sm text-gray-600 mt-1">Booked stays</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Car className="text-green-600" size={20} />
                <span className="font-medium text-gray-900">Transportation</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{travelCounts.transportation}</p>
              <p className="text-sm text-gray-600 mt-1">Bookings</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="text-purple-600" size={20} />
                <span className="font-medium text-gray-900">Documents</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{travelCounts.documents}</p>
              <p className="text-sm text-gray-600 mt-1">Travel docs</p>
            </div>
          </div>

          {/* Quick Actions */}
          <Card className="mb-6">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/travel/trips')}
                >
                  <Plane size={20} className="mr-2" />
                  Create New Trip
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/travel/passports')}
                >
                  <PassportIcon size={20} className="mr-2" />
                  Add Passport
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/travel/documents')}
                >
                  <FileText size={20} className="mr-2" />
                  Add Document
                </Button>
                <Button 
                  variant="primary" 
                  className="w-full justify-start"
                  onClick={() => window.open('https://www.booking.com', '_blank')}
                  icon={<ExternalLink size={20} />}
                >
                  Book on Booking.com
                </Button>
              </div>
            </div>
          </Card>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {navItems.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  className="cursor-pointer h-full"
                  onClick={() => handleNavigation(item.id)}
                >
                  <div className="p-6 space-y-4">
                    <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.label}</h3>
                      <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2 mt-6">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}
        </div>
      } />
      <Route path="trips/*" element={<Trips />} />
      <Route path="accommodations/*" element={<Accommodations />} />
      <Route path="transportation/*" element={<Transportation />} />
      <Route path="documents/*" element={<TravelDocuments />} />
      <Route path="passports/*" element={<Passports />} />
      <Route path="tickets/*" element={<Tickets />} />
      <Route path="itineraries/*" element={<Itineraries />} />
      <Route path="*" element={<Navigate to="/dashboard/travel" replace />} />
    </Routes>
  );
}

export default TravelManagement;
